import { type Room, RoomEvent, Track, TrackEvent } from 'livekit-client';
import { PCMAudioManager, type PCMChunk } from './pcmAudioManager';

type Role = 'doctor' | 'patient';
export interface VisitPCMFrame {
  role: Role;
  chunk: PCMChunk;
}

/** Own only clones of the call tracks. Stopping ASR must not stop the call. */
export const captureVisitPCM = (
  room: Room,
  patientIdentity: string,
  send: (frame: VisitPCMFrame) => void,
  onError: () => void
) => {
  type Source = {
    track: MediaStreamTrack;
    clone: MediaStreamTrack;
    manager: PCMAudioManager;
    ready: Promise<void>;
    accepting: boolean;
    detach?: () => void;
  };
  const sources = new Map<Role, Source>();
  const transitions = new Map<Role, Promise<void>>();
  const retire = async (source: Source) => {
    source.detach?.();
    await source.ready;
    try {
      await source.manager.stop();
    } finally {
      source.accepting = false;
      source.clone.stop();
    }
  };
  let closed = false;
  let draining = false;
  let failure = false;
  const fail = () => {
    if (closed) return;
    failure = true;
    void abort();
    onError();
  };
  const accept = (role: Role, chunk: PCMChunk) => {
    if (closed) return;
    try {
      send({ role, chunk });
    } catch {
      fail();
    }
  };
  const sync = () => {
    if (closed || draining) return;
    const participants = [
      [room.localParticipant, 'doctor'],
      [room.remoteParticipants.get(patientIdentity), 'patient'],
    ] as const;
    for (const [participant, role] of participants) {
      const publication = participant?.getTrackPublication(Track.Source.Microphone);
      const track = publication?.track?.mediaStreamTrack;
      const previous = sources.get(role);
      if (previous && track?.readyState === 'live' && previous.track === track) {
        previous.clone.enabled = track.enabled && !publication?.isMuted;
        continue;
      }
      // Each recorder owns only its cloned track; device replacement uses the same stream.
      // Muting keeps the track bound and sends silence; overlap is never erased.
      if (previous) {
        sources.delete(role);
      }
      const retired = previous ? retire(previous) : (transitions.get(role) ?? Promise.resolve());
      transitions.set(role, retired);
      if (!track || track.readyState !== 'live') {
        void retired.catch(fail);
        continue;
      }
      const clone = track.clone();
      clone.enabled = track.enabled && !publication?.isMuted;
      const manager = new PCMAudioManager({
        retainAudio: false,
        requireExistingStream: true,
        transmitSilence: true,
      });
      const source: Source = { track, clone, manager, ready: Promise.resolve(), accepting: true };
      sources.set(role, source);
      const callTrack = publication?.track;
      callTrack?.on(TrackEvent.Restarted, sync);
      source.detach = () => {
        callTrack?.off(TrackEvent.Restarted, sync);
      };
      const current = () => !closed && sources.get(role)?.manager === manager;
      manager.onStall(() => {
        if (current()) fail();
      });
      clone.addEventListener(
        'ended',
        () => {
          sync();
          if (current()) fail();
        },
        { once: true }
      );
      source.ready = retired
        .then(async () => {
          if (!current() || draining) {
            clone.stop();
            return;
          }
          await manager.start(
            undefined,
            (chunk) => {
              if (source.accepting) accept(role, chunk);
            },
            new MediaStream([clone])
          );
        })
        .catch(() => {
          source.accepting = false;
          clone.stop();
          if (current()) fail();
        });
    }
  };
  const events = [
    RoomEvent.ActiveDeviceChanged,
    RoomEvent.TrackSubscribed,
    RoomEvent.TrackUnsubscribed,
    RoomEvent.LocalTrackPublished,
    RoomEvent.LocalTrackUnpublished,
    RoomEvent.ParticipantDisconnected,
    RoomEvent.TrackMuted,
    RoomEvent.TrackUnmuted,
  ] as const;
  for (const event of events) room.on(event, sync);
  const disconnected = () => {
    void stop().catch(fail);
  };
  room.on(RoomEvent.Disconnected, disconnected);
  const detach = () => {
    for (const event of events) room.off(event, sync);
    room.off(RoomEvent.Disconnected, disconnected);
  };
  const stopSources = async () => {
    const owned = [...sources.values()];
    sources.clear();
    await Promise.all([...owned.map(retire), ...transitions.values()]);
  };
  const abort = async () => {
    if (closed) return;
    closed = true; // suppress stop()'s last frame after consent loss
    detach();
    await stopSources();
  };
  let stopPromise: Promise<void> | undefined;
  const finish = async () => {
    if (closed || draining) {
      if (failure) throw new Error('Call audio failed');
      return;
    }
    draining = true;
    detach();
    await stopSources(); // stop() transmits the last PCM before its promise resolves
    closed = true;
    if (failure) throw new Error('Call audio failed');
  };
  const stop = () => {
    if (!stopPromise) stopPromise = finish();
    return stopPromise;
  };
  sync();
  return { stop, abort };
};
